import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';
import { AdminHero, SearchBox, Count } from '@/components/admin/ui';

interface AListing {
  id: string; title: string; brand: string; code?: string; category: string;
  priceGBP: number; state: string; sellerName: string; imageUrl?: string; createdAt: string;
}

function stateChip(state: string): { label: string; pill: string; dot: string } {
  switch (state) {
    case 'ACTIVE':               return { label: 'Active',   pill: 'pill-ok',   dot: 'bg-ok' };
    case 'PENDING_VERIFICATION': return { label: 'Pending',  pill: 'pill-warn', dot: 'bg-warn' };
    case 'SOLD':                 return { label: 'Sold',     pill: 'bg-cream-deep text-mute', dot: 'bg-mute' };
    case 'REJECTED':             return { label: 'Rejected', pill: 'pill-bad',  dot: 'bg-bad' };
    case 'REMOVED':              return { label: 'Removed',  pill: 'pill-bad',  dot: 'bg-bad' };
    default:                     return { label: state.replace(/_/g, ' ').toLowerCase(), pill: 'bg-cream-deep text-mute', dot: 'bg-mute' };
  }
}

export default function AdminListings() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [state, setState] = useState('');
  const { data: rows = [], isLoading } = useQuery<AListing[]>({
    queryKey: ['admin', 'listings', state],
    queryFn: async () => (await api.get('/admin/listings', { params: { state: state || undefined } })).data,
  });
  const act = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: string }) => (await api.post(`/admin/listings/${id}/${action}`)).data,
    onSuccess: () => { toast.success('Done.'); qc.invalidateQueries({ queryKey: ['admin', 'listings'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Action failed.'),
  });

  const filtered = rows.filter((l) => !q || `${l.title} ${l.brand} ${l.code ?? ''}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-6 md:p-10 space-y-6">
      <AdminHero
        eyebrow="Admin · Catalogue"
        title="Listings & products"
        subtitle="Every listing on the marketplace. Take down or reinstate any item, live."
        stats={[
          { label: 'In view', value: filtered.length },
          { label: 'Active', value: rows.filter((l) => l.state === 'ACTIVE').length, accent: 'sage' },
          { label: 'Pending', value: rows.filter((l) => l.state === 'PENDING_VERIFICATION').length, accent: 'gold' },
        ]}
      />

      <div className="flex flex-wrap items-center gap-2">
        <SearchBox value={q} onChange={setQ} placeholder="Search title, brand or code…" className="w-72" />
        <select value={state} onChange={(e) => setState(e.target.value)} className="input w-auto text-sm">
          <option value="">All states</option>
          <option value="ACTIVE">Active</option>
          <option value="PENDING_VERIFICATION">Pending</option>
          <option value="SOLD">Sold</option>
          <option value="REJECTED">Removed / rejected</option>
        </select>
        <Count n={filtered.length} noun="listing" />
      </div>

      <div className="card divide-y divide-warmline overflow-hidden">
        {isLoading ? (
          [...Array(6)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 p-4">
              <div className="w-14 h-14 rounded-luxe bg-cream-deep animate-pulse shrink-0" />
              <div className="flex-1 space-y-2"><div className="h-3 w-24 bg-cream-deep rounded animate-pulse" /><div className="h-3.5 w-56 bg-cream-deep rounded animate-pulse" /></div>
              <div className="h-5 w-20 bg-cream-deep rounded-full animate-pulse" />
            </div>
          ))
        ) : filtered.map((l) => {
          const s = stateChip(l.state);
          return (
            <div key={l.id} className="flex flex-wrap items-center gap-4 p-4 transition-colors hover:bg-cream-deep/50">
              <div className="w-14 h-14 notched-sm bg-cover bg-center bg-cream-deep shrink-0 border border-warmline" style={l.imageUrl ? { backgroundImage: `url(${l.imageUrl})` } : undefined} />
              <div className="min-w-[160px] flex-1">
                <div className="text-[11px] text-mute mono">{l.code ?? '-'} · {l.category}</div>
                <div className="font-medium truncate">{l.brand} {l.title}</div>
                <div className="text-[11px] text-mute">Seller · {l.sellerName}</div>
              </div>
              <div className="flex flex-wrap items-center gap-4 shrink-0 ml-auto">
                <div className="display text-xl">£{l.priceGBP.toFixed(0)}</div>
                <div className="w-28"><span className={`pill text-[10px] ${s.pill}`}><span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{s.label}</span></div>
                <div className="flex gap-1.5 w-28 justify-end">
                  {l.state === 'ACTIVE' || l.state === 'PENDING_VERIFICATION' ? (
                    <button onClick={() => act.mutate({ id: l.id, action: 'takedown' })} className="btn btn-sm text-[11px] py-1 px-2.5 bg-bad text-cream hover:opacity-90">Take down</button>
                  ) : l.state === 'REJECTED' ? (
                    <button onClick={() => act.mutate({ id: l.id, action: 'reinstate' })} className="btn btn-sm btn-gold !text-ink text-[11px] py-1 px-2.5">Reinstate</button>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
        {!isLoading && !filtered.length && (
          <div className="px-5 py-16 text-center">
            <div className="text-3xl mb-2 opacity-60">🏷️</div>
            <div className="display text-lg">No listings found</div>
            <p className="text-mute text-sm mt-1">{q ? `Nothing matches “${q}”.` : 'Try a different state filter.'}</p>
          </div>
        )}
      </div>
    </div>
  );
}
